import React, { useEffect, useState } from "react";
import { X, Building2, FileText, MessageCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { adminUserDetail, apiError } from "../../lib/adminApi";
import { PlanBadge, OriginBadge, fmtDate, fmtAgo, Empty } from "./ui";

export const KIND_LABEL = {
  app_seen: "Abriu o app", login: "Login", signup: "Cadastro", quote_web: "Orçamento (web)", quote_wa: "Orçamento (WhatsApp)",
  wa_msg: "Mensagem WhatsApp", expectation: "Expectativa (pop-up)", subscription_started: "Assinatura iniciada (Stripe)", payment_failed: "Pagamento falhou (Stripe)", payment_paid: "Pagamento confirmado (Stripe)", plan_changed: "Troca de plano", stripe_stale_reset: "Vínculo Stripe antigo removido", wa_pdf: "PDF enviado no WhatsApp", pdf_web: "PDF baixado (web)", member_created: "Membro criado (admin)", member_updated: "Membro editado (admin)",
};

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl bg-white/[0.03] ring-1 ring-white/[0.06] p-3">
    <div className="flex items-center gap-1.5 text-[11px] text-slate-400"><Icon size={12} /> {label}</div>
    <div className="mt-1 text-lg font-bold font-mono-jb">{value}</div>
  </div>
);

export default function UserDetail({ userId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { adminUserDetail(userId).then(setD).catch((e) => toast.error(apiError(e))); }, [userId]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-[2px]" data-testid="user-detail">
      <div className="w-full max-w-xl h-full overflow-y-auto bg-[#0D1220] ring-1 ring-white/10 p-6 sm:p-7">
        <div className="flex items-start justify-between gap-4">
          {d ? (
            <div>
              <h2 className="text-xl font-bold">{d.user.name || d.user.email}</h2>
              <div className="text-[13px] text-slate-400">{d.user.email}</div>
              <div className="mt-2 flex flex-wrap items-center gap-2"><PlanBadge plan={d.user.effective_plan} /><OriginBadge origin={d.user.origin} />{d.user.disabled && <span className="text-[11px] text-rose-300">bloqueado</span>}</div>
            </div>
          ) : <div className="text-slate-500 text-[13px]">Carregando…</div>}
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="user-detail-close"><X size={18} /></button>
        </div>
        {d && (
          <>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat icon={FileText} label="Orçamentos" value={d.usage.quotes} />
              <Stat icon={Building2} label="Empresas" value={d.usage.companies} />
              <Stat icon={MessageCircle} label="Msgs WA 30d" value={d.usage.wa_msgs_30d} />
              <Stat icon={Clock} label="Dias ativos 30d" value={d.usage.active_days_30d} />
            </div>
            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-[12.5px]">
              <dt className="text-slate-500">Cadastro</dt><dd>{fmtDate(d.user.created_at, true)}</dd>
              <dt className="text-slate-500">Último acesso</dt><dd>{fmtAgo(d.user.last_seen_at)}</dd>
              <dt className="text-slate-500">Plano expira</dt><dd>{d.user.plan_expires_at ? fmtDate(d.user.plan_expires_at) : "—"}</dd>
              <dt className="text-slate-500">WhatsApp</dt><dd>{d.usage.wa_phones.length ? d.usage.wa_phones.join(", ") : "não vinculado"}</dd>
              {d.user.note && <><dt className="text-slate-500">Observação</dt><dd>{d.user.note}</dd></>}
            </dl>

            <h3 className="mt-6 text-[11px] uppercase tracking-wider text-slate-400">Empresas</h3>
            {d.companies.length ? <ul className="mt-2 space-y-1 text-[13px]">{d.companies.map((c) => <li key={c.id} className="flex justify-between"><span>{c.name || "Sem nome"}</span><span className="text-slate-500">{c.layout}</span></li>)}</ul> : <Empty text="Nenhuma empresa" />}

            <h3 className="mt-6 text-[11px] uppercase tracking-wider text-slate-400">Últimos orçamentos</h3>
            {d.quotes.length ? (
              <ul className="mt-2 divide-y divide-white/[0.05] text-[13px]">
                {d.quotes.map((q) => (
                  <li key={q.id} className="py-2 flex items-center justify-between gap-3">
                    <span className="truncate">{q.client_name || q.title || "—"} <span className="text-slate-500">· {q.title}</span></span>
                    <span className="shrink-0 text-[11px] text-slate-500">{q.source === "whatsapp" ? "WhatsApp" : "Web"} · {fmtDate(q.created_at)}</span>
                  </li>
                ))}
              </ul>
            ) : <Empty text="Nenhum orçamento" />}

            <h3 className="mt-6 text-[11px] uppercase tracking-wider text-slate-400">Atividade recente</h3>
            {d.events.length ? (
              <ul className="mt-2 space-y-1.5 text-[12.5px]">
                {d.events.map((e, i) => (
                  <li key={i} className="flex items-center justify-between gap-3">
                    <span>{KIND_LABEL[e.kind] || e.kind}{e.meta?.client ? <span className="text-slate-500"> · {e.meta.client}</span> : null}{e.meta?.text ? <span className="text-slate-500"> · “{e.meta.text}”</span> : null}</span>
                    <span className="shrink-0 text-slate-500">{fmtDate(e.at, true)}</span>
                  </li>
                ))}
              </ul>
            ) : <Empty text="Sem atividade registrada ainda" />}
          </>
        )}
      </div>
    </div>
  );
}
